import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { JavapiService } from '../../services/javapi.service';
import { Metodosui } from '../../services/metodosui';
import { firstValueFrom } from 'rxjs';

export interface ChatMessage {
  id: string;
  sender: 'user' | 'ia';
  text: string;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'error';
}

@Component({
  selector: 'app-helpia',
  templateUrl: './helpia.component.html',
  styleUrls: ['./helpia.component.scss'],
  standalone: false
})
export class HelpiaComponent implements OnInit {
  @Input() moduloContexto: string = 'General';
  @Input() datosContexto: any = null;
  @ViewChild('chatScroll', { static: false }) chatScroll?: ElementRef;

  queryText: string = '';
  loading: boolean = false;
  statusMessage: string = '';
  userName: string = 'Usuario';

  messages: ChatMessage[] = [];

  // Preguntas frecuentes o atajos rápidos sugeridos
  quickPrompts: string[] = [
    '¿Cómo registro una nueva inspección con áreas y medidas?',
    '¿Cómo calculo el rendimiento de pintura para un metraje cuadrado?',
    '¿Cuál es el proceso para cambiar de cotización a trabajo en proceso?',
    '¿Cómo subir una factura y asociarla a los gastos?'
  ];

  constructor(
    private modalCtrl: ModalController,
    private javapiService: JavapiService,
    private metodosUi: Metodosui
  ) {}

  ngOnInit() {
    this.obtenerUsuario();
    this.iniciarConversacion();
  }

  trackByMsgId(index: number, msg: ChatMessage): string {
    return msg ? msg.id : String(index);
  }

  obtenerUsuario() {
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        this.userName = user.l_nombre || user.l_usuario || 'Usuario';
      }
    } catch (e) {
      console.warn('Error leyendo usuario:', e);
    }
  }

  iniciarConversacion() {
    const bienvenida = `¡Hola, ${this.userName}! Soy PG IA, tu asistente técnico de Pinturas García. ¿En qué te puedo colaborar hoy respecto a ${this.moduloContexto.toLowerCase()} u operaciones de obra?`;
    this.messages.push({
      id: 'msg_0',
      sender: 'ia',
      text: bienvenida,
      timestamp: new Date()
    });
  }

  seleccionarPrompt(prompt: string) {
    this.queryText = prompt;
    this.enviarConsulta();
  }

  async enviarConsulta() {
    const cleanText = this.queryText.trim();
    if (!cleanText || this.loading) return;

    const userMsgId = 'msg_' + Date.now();
    this.messages.push({
      id: userMsgId,
      sender: 'user',
      text: cleanText,
      timestamp: new Date()
    });

    this.queryText = '';
    this.loading = true;
    this.statusMessage = 'PG IA está procesando tu respuesta...';
    this.scrollToBottom();

    try {
      // 1. Obtener configuración de IA desde pg_parametros_json
      const paramRes: any = await firstValueFrom(
        this.javapiService.getJsonParam('CONFIG_IA_HELPIA')
      ).catch(() => null);

      let model = 'gemini-3.6-flash';
      let temperature = 0.2;
      let systemInstruction = 'Eres PG IA, asistente técnico y operativo de Pinturas García.';
      let promptTemplate = 'El usuario consulta: {CONSULTA}';
      let freeAttempts = 2;
      let payAttempts = 1;
      let forcePay = false;

      if (paramRes && (paramRes.pa_json || paramRes.dt3)) {
        try {
          const raw = paramRes.pa_json || paramRes.dt3;
          const cfg = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (cfg.model) model = cfg.model;
          if (cfg.temperature !== undefined) temperature = Number(cfg.temperature);
          if (cfg.system_instruction) systemInstruction = cfg.system_instruction;
          if (cfg.prompt_template) promptTemplate = cfg.prompt_template;
          if (cfg.free_attempts !== undefined) freeAttempts = Number(cfg.free_attempts);
          if (cfg.pay_attempts !== undefined) payAttempts = Number(cfg.pay_attempts);
          if (cfg.force_pay !== undefined) forcePay = Boolean(cfg.force_pay);
        } catch (e) {
          console.error('Error parseando CONFIG_IA_HELPIA:', e);
        }
      }

      // 2. Construir el prompt dinámico con contexto
      let contextoStr = `Módulo actual: ${this.moduloContexto}`;
      if (this.datosContexto) {
        contextoStr += ` | Contexto específico: ${typeof this.datosContexto === 'object' ? JSON.stringify(this.datosContexto) : this.datosContexto}`;
      }

      const finalPrompt = promptTemplate
        .replace('{USUARIO}', this.userName)
        .replace('{CONSULTA}', cleanText)
        .replace('{CONTEXTO}', contextoStr);

      // 3. Estrategia de reintentos (Gratis -> Pago)
      const attemptsList: boolean[] = [];
      if (forcePay) {
        for (let i = 0; i < payAttempts; i++) attemptsList.push(true);
      } else {
        for (let i = 0; i < freeAttempts; i++) attemptsList.push(false);
        for (let i = 0; i < payAttempts; i++) attemptsList.push(true);
      }
      if (attemptsList.length === 0) attemptsList.push(false);

      let iaResponse: any = null;
      let success = false;
      const totalAttempts = attemptsList.length;

      for (let idx = 0; idx < totalAttempts; idx++) {
        const usePayKey = attemptsList[idx];
        const attempt = idx + 1;
        this.statusMessage = usePayKey
          ? `Consultando con IA (Canal de alta disponibilidad)...`
          : `Consultando respuesta (${attempt}/${totalAttempts})...`;

        try {
          iaResponse = await firstValueFrom(
            this.javapiService.generarAnalisisIa(finalPrompt, {
              model,
              systemInstruction,
              temperature,
              usePayKey,
              attempt,
              totalAttempts
            })
          );

          // Manejo robusto de la respuesta según el contrato del backend ({ success, data, raw })
          if (iaResponse) {
            const rawText = iaResponse.raw || (typeof iaResponse === 'string' ? iaResponse : null);
            const dataObj = iaResponse.data || iaResponse;

            let extractedText = '';
            if (dataObj && typeof dataObj === 'object') {
              extractedText = dataObj.respuesta || dataObj.analisis || dataObj.diagnostico_general || dataObj.resultado || '';
            }
            if (!extractedText && rawText) {
              extractedText = rawText;
            }

            if (extractedText && extractedText.trim()) {
              iaResponse._finalText = extractedText.trim();
              success = true;
              break;
            }
          }
        } catch (err) {
          console.warn(`Intento ${attempt} fallido:`, err);
        }
      }

      let finalText = '';
      if (success && iaResponse && iaResponse._finalText) {
        finalText = iaResponse._finalText;
      } else if (iaResponse && iaResponse.raw) {
        finalText = iaResponse.raw;
      } else {
        finalText = 'Disculpa, no pude procesar tu solicitud en este momento. Por favor intenta reformular tu pregunta o verifica la conexión.';
      }

      this.messages.push({
        id: 'msg_' + Date.now(),
        sender: 'ia',
        text: finalText,
        timestamp: new Date()
      });

    } catch (err: any) {
      console.error('Error general en HelpIA:', err);
      this.messages.push({
        id: 'msg_' + Date.now(),
        sender: 'ia',
        text: 'Ocurrió un inconveniente al consultar el servicio de IA. Intenta de nuevo en unos segundos.',
        timestamp: new Date(),
        status: 'error'
      });
    } finally {
      this.loading = false;
      this.statusMessage = '';
      this.scrollToBottom();
    }
  }

  scrollToBottom() {
    setTimeout(() => {
      if (this.chatScroll && this.chatScroll.nativeElement) {
        this.chatScroll.nativeElement.scrollTop = this.chatScroll.nativeElement.scrollHeight;
      }
    }, 120);
  }

  copiarTexto(texto: string) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(texto).then(() => {
        this.metodosUi.showToast('Respuesta copiada al portapapeles', 'success', 'bottom');
      });
    }
  }

  cerrar() {
    this.modalCtrl.dismiss();
  }
}
